import { useRequestPasswordReset } from '@lopo-tech/lopo-api'
import React, { useCallback, useState } from 'react'
import { Button } from 'src/components/button/button'
import InputField from 'src/components/forms/input_field/input_field'
import { SvgIcon } from 'src/components/icons/svg-icon'
import MessageSegment from 'src/components/message_segment/message_segment'
import { CLASS_PREFIX } from 'src/constants'
import { REDIRECT_URL_PASSWORD_RESET } from 'src/constants/urls'
import { textResources } from 'src/lang/de'
import { UrlUtils } from 'src/utils/url/url'
import { validateEmail } from 'src/utils/validation'
import { AUTH_CLS } from '../../auth/constants'
import './forgot_password_form.scss'

const cls = `${CLASS_PREFIX}forgot-password-form`

type IProps = {
  onBack: () => void
  onClose: () => void
  email?: string
}

export const ForgotPasswordForm: React.FC<IProps> = ({ onBack, onClose, email: initialEmail }) => {
  const [email, setEmail] = useState(initialEmail ?? '')
  const [validEmail, setValidEmail] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [requestPasswordReset] = useRequestPasswordReset()

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    setValidEmail(true)
    setErrorMessage('')
  }, [])

  const handleSubmit = useCallback(() => {
    const isValid = validateEmail(email)
    if (!isValid) {
      setValidEmail(false)
      return
    }

    setIsLoading(true)
    const redirectUrl = UrlUtils.getCurrentUrlOrigin() + REDIRECT_URL_PASSWORD_RESET

    requestPasswordReset({ variables: { input: { email, redirectUrl } } })
      .then((response) => {
        const error = response.error
        if (error) {
          setValidEmail(false)
          setErrorMessage(error.message)
          return
        }
        setShowSuccess(true)
      })
      .catch(() => {
        setErrorMessage(textResources.shared.errorUnknown)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [email, requestPasswordReset])

  if (showSuccess) {
    return (
      <div className={`${cls}__success`}>
        <button className={`${cls}__success__close`} onClick={onClose} type="button">
          <SvgIcon name="General/x" size={20} />
        </button>
        <MessageSegment
          headerIcon='confirm-email'
          title={textResources.authentication.resetConfirmationTitle}
          message={textResources.authentication.resetConfirmationBody}
        />
      </div>
    )
  }

  return (
    <div className={cls}>
      <div className={`${cls}__header`}>
        <button onClick={onBack}>
          <SvgIcon name="Arrows/chevron-left" /> {textResources.shared.back}
        </button>
      </div>
      <div className={`${cls}__content`}>
        <div className={`${AUTH_CLS}__header`}>{textResources.authentication.passwordForgot}</div>
        <div className={`${AUTH_CLS}__subheader`}>{textResources.authentication.passwordForgotBody}</div>
        <InputField
          floatLabel={false}
          name="email"
          label={textResources.shared.email}
          placeholder={textResources.authentication.enterEmail}
          autoComplete="username"
          value={email}
          onChange={handleEmailChange}
          valid={validEmail}
          errorText={!validEmail ? textResources.shared.errorEmail : undefined}
        />
        {errorMessage && <div className={`${cls}__error`}>{errorMessage}</div>}
        <Button
          size='xlarge'
          title={textResources.authentication.sendEmail}
          disabled={isLoading}
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}
